import React, { useState, useEffect } from 'react';
import { authHelper } from '../utils/authHelper';

const API = process.env.REACT_APP_BACKEND_URL;

function LMSDashboard() {
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    checkAccessAndLoadCourses();
  }, []);

  const checkAccessAndLoadCourses = async () => {
    try {
      // Check if user has LMS access
      const accessRes = await fetch(`${API}/api/auth/user-access`, {
        headers: authHelper.getAuthHeaders()
      });
      const accessData = await accessRes.json();
      
      // Check for web_platform access (LMS is part of web platform)
      if (!accessData.has_web_platform && !accessData.is_admin) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      setHasAccess(true);

      // Load courses
      const coursesRes = await fetch(`${API}/api/lms/courses`, {
        headers: authHelper.getAuthHeaders()
      });
      const coursesData = await coursesRes.json();
      setCourses(coursesData.courses || []);

      // Load progress
      const progressRes = await fetch(`${API}/api/lms/progress`, {
        headers: authHelper.getAuthHeaders()
      });
      const progressData = await progressRes.json();
      setProgress(progressData.progress || []);

    } catch (err) {
      console.error('Error loading LMS data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getProgressForCourse = (courseId) => {
    return progress.find(p => p.course_id === courseId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
        <p className="text-white text-xl">Cargando...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-gray-800 rounded-lg p-8 text-center">
          <span className="text-6xl mb-4 block">🔒</span>
          <h2 className="text-3xl font-bold text-white mb-4">Acceso Denegado</h2>
          <p className="text-gray-400 mb-6">
            Necesitas la <strong className="text-[#C5A14C]">Plataforma Web ($2500)</strong> para acceder a los cursos.
          </p>
          <button
            onClick={() => window.location.href = '#/'}
            className="px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
          >
            Ver Planes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#02040A]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#C5A14C] to-[#B89140] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-white mb-2">🎓 Plataforma de Cursos</h1>
          <p className="text-gray-100">Bienvenido a Método 11 - Aprende trading profesional</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* My Progress Section */}
        {progress.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">📊 Mi Progreso</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {progress.map(prog => (
                <div key={prog.progress_id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-2">{prog.course_title}</h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Inscrito: {new Date(prog.enrollment_date).toLocaleDateString()}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Progreso</span>
                      <span className="text-[#C5A14C] font-semibold">{prog.overall_progress}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-[#C5A14C] to-[#B89140] h-3 rounded-full transition-all duration-300"
                        style={{ width: `${prog.overall_progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                    <span>✅ {prog.completed_lessons?.length || 0} lecciones completadas</span>
                  </div>

                  <button
                    onClick={() => window.location.href = `#/lms/course/${prog.course_id}`}
                    className="w-full px-4 py-2 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
                  >
                    Continuar Aprendiendo
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Available Courses */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">📚 Cursos Disponibles</h2>
          
          {courses.length === 0 ? (
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <span className="text-6xl mb-4 block">📖</span>
              <p className="text-gray-400 text-lg">No hay cursos disponibles todavía</p>
              <p className="text-gray-500 text-sm mt-2">Los cursos se publicarán pronto</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map(course => {
                const courseProgress = getProgressForCourse(course.course_id);
                const isEnrolled = !!courseProgress;

                return (
                  <div key={course.course_id} className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-[#C5A14C] transition-colors">
                    {/* Thumbnail */}
                    <div className="h-48 bg-gradient-to-br from-[#C5A14C] to-[#02040A] flex items-center justify-center">
                      {course.thumbnail_url ? (
                        <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-7xl">📚</span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <h3 className="text-xl font-bold text-white mb-2">{course.title}</h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-3">{course.description}</p>

                      {isEnrolled ? (
                        <div className="mb-4">
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>Tu progreso</span>
                            <span className="text-[#C5A14C] font-semibold">{courseProgress.overall_progress}%</span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-[#C5A14C] h-2 rounded-full"
                              style={{ width: `${courseProgress.overall_progress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4">
                          <span className="inline-block px-3 py-1 bg-green-900 text-green-300 rounded text-sm">
                            ✨ Nuevo para ti
                          </span>
                        </div>
                      )}

                      <button
                        onClick={() => window.location.href = `#/lms/course/${course.course_id}`}
                        className="w-full px-4 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors font-semibold"
                      >
                        {isEnrolled ? 'Continuar' : 'Comenzar Curso'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LMSDashboard;
